/**
 * Shared tessellation helpers for arc / ellipse / spline / bulge
 * adapters.
 *
 * # Why the segment count is camera-aware (Q22 LOD)
 *
 * A small radius circle at zoom 0.01× shouldn't generate 256 segments —
 * the user sees a 4-pixel disc and a hexagon would suffice. A 100 m
 * radius arc at zoom 100× absolutely needs 256 segments — otherwise
 * the polygonal silhouette becomes obvious. The formula
 *
 *   N = clamp(2, ceil(arcLength * cameraZoom / 4), 256)
 *
 * matches the heuristic used by AutoCAD's arc tessellator (4 px per
 * segment at 96 DPI). The lower bound `2` keeps the API consistent —
 * an arc always has at least one segment (= 2 vertices).
 *
 * # Bulge → arc decoding
 *
 * AutoCAD's LWPOLYLINE bulge is `tan(swept_angle / 4)` over a chord.
 * `bulgeToArc` converts a bulge value + chord endpoints into the
 * (center, radius, startAngle, endAngle, ccw) tuple expected by the
 * arc tessellator. This is the same math used by `entityBounds.ts` and
 * the dxf-io polyline tessellator — keep them in sync.
 */

import type { Vec3 } from "../types";

/** Minimum segments per primitive (arc with degenerate length). */
export const MIN_TESSELLATION_SEGMENTS = 2;
/** Maximum segments — caps GPU memory at high zoom on tiny arcs. */
export const MAX_TESSELLATION_SEGMENTS = 256;

/**
 * Compute the tessellation segment count for an arc / ellipse / circle
 * / spline of the given world-space arc length at the given camera
 * zoom. `cameraZoom` is the linear zoom factor (1 = 1 world-unit per
 * px); `4` in the divisor is the target screen-space pixels per
 * segment.
 */
export function tessellationSegmentCount(
  arcLengthWorld: number,
  cameraZoom: number,
): number {
  const px = Math.max(0, arcLengthWorld) * Math.max(0, cameraZoom);
  const ideal = Math.ceil(px / 4);
  if (!Number.isFinite(ideal) || ideal < MIN_TESSELLATION_SEGMENTS) {
    return MIN_TESSELLATION_SEGMENTS;
  }
  if (ideal > MAX_TESSELLATION_SEGMENTS) return MAX_TESSELLATION_SEGMENTS;
  return ideal;
}

/** Polygon vertex stream returned by every tessellator below. */
export type Polyline = Vec3[];

/**
 * Sample an arc at `count + 1` vertices (so `count` segments) from
 * `startAngle` to `endAngle` (radians). `ccw=true` walks the angles
 * forward; `ccw=false` walks them backward.
 */
export function tessellateArc(
  centerX: number,
  centerY: number,
  centerZ: number,
  radius: number,
  startAngle: number,
  endAngle: number,
  ccw: boolean,
  count: number,
): Polyline {
  const segments = Math.max(MIN_TESSELLATION_SEGMENTS, count);
  let sweep = endAngle - startAngle;
  if (ccw) {
    if (sweep < 0) sweep += Math.PI * 2;
  } else {
    if (sweep > 0) sweep -= Math.PI * 2;
  }
  const out: Polyline = [];
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const angle = startAngle + sweep * t;
    out.push({
      x: centerX + radius * Math.cos(angle),
      y: centerY + radius * Math.sin(angle),
      z: centerZ,
    });
  }
  return out;
}

/** The implicit OCS extrusion normal of an ellipse that OMITS 210/220/230. */
const DEFAULT_ELLIPSE_NORMAL: Vec3 = { x: 0, y: 0, z: 1 };

/**
 * The UNIT in-plane minor-axis direction of an ellipse — `extrusion × majorAxis`
 * normalised (ezdxf `entities/ellipse.py` `minor_axis`). The ONE-TRUTH minor
 * direction shared by the RENDER sampler ({@link tessellateEllipse}) and the
 * INTERACTION consumers (SL-15 round-8 @codex F4): the pick (`raycast/distance`),
 * window/crossing selection (`raycast/windowSelect`) and OSNAP
 * (`snap/osnapCandidates`) all place the minor axis via THIS, so a tilted ellipse
 * is picked / windowed / snapped exactly where it renders.
 *
 * For a PLANAR ellipse (default (0,0,1) normal, major in XY) this reduces to the
 * XY rot90 `(−ŷ, x̂, 0)` the callers used before — byte-identical. Degenerate
 * (major ∥ extrusion, ill-defined plane) falls back to ANY unit ⟂ the major so
 * the samplers never emit NaN.
 */
export function ellipseMinorUnit(
  majorAxis: Vec3,
  extrusion: Vec3 = DEFAULT_ELLIPSE_NORMAL,
): Vec3 {
  const mx = extrusion.y * majorAxis.z - extrusion.z * majorAxis.y;
  const my = extrusion.z * majorAxis.x - extrusion.x * majorAxis.z;
  const mz = extrusion.x * majorAxis.y - extrusion.y * majorAxis.x;
  const mlen = Math.hypot(mx, my, mz);
  if (mlen > 1e-12) {
    return { x: mx / mlen, y: my / mlen, z: mz / mlen };
  }
  // Degenerate: major ∥ extrusion. Pick any unit ⟂ x̂ (x̂ × ref).
  const rx = Math.hypot(majorAxis.x, majorAxis.y, majorAxis.z) || 1;
  const ax: Vec3 = {
    x: majorAxis.x / rx,
    y: majorAxis.y / rx,
    z: majorAxis.z / rx,
  };
  const ref: Vec3 =
    Math.abs(ax.z) < 0.9 ? { x: 0, y: 0, z: 1 } : { x: 1, y: 0, z: 0 };
  const px = ax.y * ref.z - ax.z * ref.y;
  const py = ax.z * ref.x - ax.x * ref.z;
  const pz = ax.x * ref.y - ax.y * ref.x;
  const plen = Math.hypot(px, py, pz) || 1;
  return { x: px / plen, y: py / plen, z: pz / plen };
}

/**
 * Sample an ellipse arc at `count + 1` vertices — FULL 3D (SL-15 F-C).
 *
 * `majorAxis` is the FULL 3D vector from `center` to the major-axis endpoint
 * (its z tilts the plane). `ratio` is `minor/major`. `start` / `end` are in
 * radians, CCW from the major axis. `extrusion` is the OCS normal (default
 * (0,0,1)) that fixes the minor-axis DIRECTION.
 *
 * Matches ezdxf `entities/ellipse.py` vertex generation:
 *   radius_x = |major_axis|          (full 3D magnitude — NOT hypot(x,y))
 *   x̂        = major_axis / rx
 *   minor    = extrusion × major_axis   (perpendicular, in the ellipse plane)
 *   ŷ        = minor / |minor|
 *   radius_y = radius_x · ratio
 *   vertex(θ) = center + x̂·cos(θ)·rx + ŷ·sin(θ)·ry
 *
 * For a PLANAR ellipse (major.z = 0, default (0,0,1) normal) this is IDENTICAL to
 * the prior 2D formula (x̂ = (cos,sin,0), ŷ = (−sin,cos,0), z ≡ center.z) — golden
 * parity for every existing planar ellipse.
 */
export function tessellateEllipse(
  center: Vec3,
  majorAxis: Vec3,
  ratio: number,
  startParam: number,
  endParam: number,
  count: number,
  extrusion: Vec3 = DEFAULT_ELLIPSE_NORMAL,
): Polyline {
  const segments = Math.max(MIN_TESSELLATION_SEGMENTS, count);
  const rx = Math.hypot(majorAxis.x, majorAxis.y, majorAxis.z);
  if (rx === 0) return [];
  const ry = rx * ratio;
  // x̂ — the normalized major-axis direction (full 3D).
  const ax: Vec3 = {
    x: majorAxis.x / rx,
    y: majorAxis.y / rx,
    z: majorAxis.z / rx,
  };
  // ŷ — the normalized in-plane minor direction (extrusion × major, ezdxf
  // `minor_axis`), the ONE-TRUTH shared with the interaction consumers.
  const ay = ellipseMinorUnit(majorAxis, extrusion);
  let sweep = endParam - startParam;
  // ELLIPSE in DXF is always CCW in parameter space; normalise to a positive sweep.
  if (sweep < 0) sweep += Math.PI * 2;
  if (sweep === 0) sweep = Math.PI * 2;
  const out: Polyline = [];
  for (let i = 0; i <= segments; i++) {
    const t = i / segments;
    const angle = startParam + sweep * t;
    const cx = Math.cos(angle) * rx;
    const sy = Math.sin(angle) * ry;
    out.push({
      x: center.x + ax.x * cx + ay.x * sy,
      y: center.y + ax.y * cx + ay.y * sy,
      z: center.z + ax.z * cx + ay.z * sy,
    });
  }
  return out;
}

/** Result of `bulgeToArc`. Suitable input for `tessellateArc`. */
export interface BulgeArc {
  centerX: number;
  centerY: number;
  radius: number;
  startAngle: number;
  endAngle: number;
  ccw: boolean;
}

/**
 * Convert a bulge value + chord endpoints to an arc. AutoCAD bulge
 * convention: positive bulge = CCW arc (left of chord direction),
 * negative bulge = CW arc.
 *
 * Returns `null` for a bulge of 0 or a degenerate chord — the caller
 * should fall back to a straight line in that case.
 */
export function bulgeToArc(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  bulge: number,
): BulgeArc | null {
  if (bulge === 0) return null;
  const chordX = bx - ax;
  const chordY = by - ay;
  const chordLen = Math.hypot(chordX, chordY);
  if (chordLen === 0) return null;
  const sagittaSign = Math.sign(bulge);
  const halfChord = chordLen / 2;
  // bulge = tan(theta/4) where theta is the swept angle. Hence
  // theta = 4 * atan(|bulge|), radius = halfChord / sin(theta/2).
  const theta = 4 * Math.atan(Math.abs(bulge));
  const radius = halfChord / Math.sin(theta / 2);
  // Center perpendicular to chord, on the sagitta-sign side. For an
  // arc in CCW direction (positive bulge), the center is to the left
  // of the chord (bulgeSign +1 → negative perpendicular when our Y
  // axis is up — same sign convention as the dxf-io tessellator).
  const midX = (ax + bx) / 2;
  const midY = (ay + by) / 2;
  // Distance from midpoint to center: sqrt(r^2 - (chord/2)^2).
  // For |bulge|=1 (semicircle) this is 0; for |bulge|<1 (arc < 180°)
  // it is positive; for |bulge|>1 (arc > 180°) it is again positive
  // but the center is on the *opposite* side of the chord from the
  // arc's outermost point — handled by sign flip on (>180° case).
  const major = Math.abs(bulge) > 1; // arc > 180° → centre on the chord-side
  const distToCenter = Math.sqrt(radius * radius - halfChord * halfChord);
  // Perpendicular to chord, normalised, with sign based on bulge.
  let nx = -chordY / chordLen;
  let ny = chordX / chordLen;
  if (sagittaSign < 0) {
    nx = -nx;
    ny = -ny;
  }
  if (major) {
    nx = -nx;
    ny = -ny;
  }
  const centerX = midX + nx * distToCenter;
  const centerY = midY + ny * distToCenter;
  const startAngle = Math.atan2(ay - centerY, ax - centerX);
  const endAngle = Math.atan2(by - centerY, bx - centerX);
  return {
    centerX,
    centerY,
    radius,
    startAngle,
    endAngle,
    ccw: sagittaSign > 0,
  };
}

/**
 * Tessellate one bulged segment of a polyline (chord + bulge → polyline
 * vertices, exclusive of the start vertex — caller pushes it). Returns
 * `[bx, by]` as a singleton when bulge = 0 (straight segment).
 */
export function tessellateBulgeSegment(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  bulge: number,
  cameraZoom: number,
  z = 0,
): Polyline {
  if (bulge === 0) return [{ x: bx, y: by, z }];
  const arc = bulgeToArc(ax, ay, bx, by, bulge);
  if (!arc) return [{ x: bx, y: by, z }];
  const arcLen = arc.radius *
    Math.abs(arc.endAngle - arc.startAngle + (arc.ccw ? 0 : 0));
  const count = tessellationSegmentCount(
    Math.max(arcLen, Math.hypot(bx - ax, by - ay)),
    cameraZoom,
  );
  const verts = tessellateArc(
    arc.centerX,
    arc.centerY,
    z,
    arc.radius,
    arc.startAngle,
    arc.endAngle,
    arc.ccw,
    count,
  );
  // Drop the duplicate start vertex — caller already emitted it.
  return verts.slice(1);
}
